import { useState, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'

interface OperationProgressState {
  operationId: string | null
  isProgressOpen: boolean
  isLoading: boolean
}

export function useBulkOperationProgress() {
  const [state, setState] = useState<OperationProgressState>({
    operationId: null,
    isProgressOpen: false,
    isLoading: false
  })

  const startOperation = useCallback(async (
    operationType: string,
    totalSelected: number,
    operationDetails: any = {},
    performedBy: string = 'User'
  ) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))

      const { data, error } = await supabase
        .from('mailbox_bulk_operations')
        .insert({
          operation_type: operationType,
          total_selected: totalSelected,
          operation_details: operationDetails,
          performed_by: performedBy,
        })
        .select()
        .single()

      if (error) throw error

      setState(prev => ({
        ...prev,
        operationId: data.id,
        isProgressOpen: true,
        isLoading: false
      }))

      return data.id
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }))
      throw error
    }
  }, [])

  const updateOperation = useCallback(async (
    operationId: string,
    updates: {
      successful_count?: number
      failed_count?: number
      errors?: string[]
      completed_at?: string
      operation_details?: any
      progress_details?: any
    }
  ) => {
    try {
      const { error } = await supabase
        .from('mailbox_bulk_operations')
        .update({
          ...updates,
          completed_at: updates.completed_at || new Date().toISOString(),
        })
        .eq('id', operationId)

      if (error) throw error
    } catch (error) {
      console.error('Error updating operation:', error)
      throw error
    }
  }, [])

  const closeProgress = useCallback(() => {
    setState(prev => ({ ...prev, isProgressOpen: false }))
  }, [])

  const openProgress = useCallback(() => {
    setState(prev => ({ ...prev, isProgressOpen: true }))
  }, [])

  const onOperationComplete = useCallback(() => {
    toast.success('Operation completed successfully')
  }, [])

  // Check for active operations on mount - with intelligent filtering
  const checkActiveOperations = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('mailbox_bulk_operations')
        .select('*')
        .is('completed_at', null)
        .order('started_at', { ascending: false })
        .limit(1)

      if (error) throw error

      if (data && data.length > 0) {
        const activeOperation = data[0]
        const startedAt = new Date(activeOperation.started_at)
        const now = new Date()
        const timeDiff = now.getTime() - startedAt.getTime()
        const minutesElapsed = timeDiff / (1000 * 60)

        // Only auto-open if operation started within last 5 minutes OR has recent progress
        const shouldAutoOpen = minutesElapsed < 5 || 
          (activeOperation.successful_count > 0 || activeOperation.failed_count > 0)

        if (shouldAutoOpen && minutesElapsed < 30) { // Don't auto-open operations older than 30 minutes
          setState(prev => ({
            ...prev,
            operationId: activeOperation.id,
            isProgressOpen: true
          }))
          toast.info('Found active operation - resuming progress tracking')
        }
      }
    } catch (error) {
      console.error('Error checking active operations:', error)
    }
  }, [])

  return {
    ...state,
    startOperation,
    updateOperation,
    closeProgress,
    openProgress,
    onOperationComplete,
    checkActiveOperations
  }
}